import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { OauthProvideActions } from "@states/oauth-provider/oauth-provider.action-types";
import { catchError, debounceTime, exhaustMap, of, share, switchMap, withLatestFrom } from "rxjs";
import { OrganizationService } from "src/app/development/organization.service";
import { TranslateService } from "@ngx-translate/core";
import { SharedActions } from "@states/shared/shared.action-types";
import { AppState } from "../app.state";
import { OrganizationSelectors } from "@states/organization/organization.selector-types";
import { UtilsService } from "src/app/edge/utils.service";
import { LoggerService } from "src/app/services/logger.service";
import { ContextualLoggerService } from "src/app/services/contextual-logger.service";

@Injectable()
export class OAuthProviderEffect {
    private baseLogger = inject(LoggerService);
    private context = 'OauthProviderEffect';
    private logger = new ContextualLoggerService(this.baseLogger, this.context);

    public startLoadingOauthProviders = createEffect(() =>
        this.actions$.pipe(
            ofType(OauthProvideActions.startLoadingOauthProviders),
            exhaustMap(() => [OauthProvideActions.setIsLoading({ isLoading: true }), OauthProvideActions.getOauthProviders()]),
        ),
    );

    public getOauthProviders$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OauthProvideActions.getOauthProviders),
            withLatestFrom(
                this.store$.pipe(select(state => state.oauthProviderState)),
                this.store$.pipe(select(OrganizationSelectors.selectActiveOrganization))
            ),
            switchMap(([, { page, perPage, query, filters, isLastPage }, { orgId }]) => {
                if (!isLastPage) {
                    return this.organizationService.getOauthProviders(page, perPage, query, filters, orgId).pipe(
                        switchMap((res) => {
                            return [
                                OauthProvideActions.getOauthProvidersSuccess({ oauthProviders: res }),
                                OauthProvideActions.setIsLoading({ isLoading: false }),
                            ]
                        }),
                        catchError((response) => {
                            const message = this.utilsService.errMessage(response);
                            this.logger.error(message);

                            return [
                                OauthProvideActions.setIsLoading({ isLoading: false }),
                            ];
                        })
                    )
                } else {
                    return of(OauthProvideActions.setIsLoading({ isLoading: false }));
                }
            }),
            share(),
        )
    );

    public createOauthProvider$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OauthProvideActions.createOauthProvider),
            exhaustMap(({ dto }) =>
                [
                    OauthProvideActions.setIsSaving({ isSaving: true }),
                    OauthProvideActions.createOauthProviderServerCall({ dto }),
                ],
            ),
            share(),
        ),
    );

    public createOauthProviderServerCall$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OauthProvideActions.createOauthProviderServerCall),
            switchMap(({ dto }) =>
                this.organizationService.createOauthProvider(dto)
                    .pipe(
                        switchMap(res => {
                            return [
                                OauthProvideActions.createOauthProviderSuccess(),
                                SharedActions.showMessage({ success: this.translateService.instant('OAUTH_PROVIDER_TOASTS.CREATED_SUCCESS') }),
                                OauthProvideActions.resetList(),
                                OauthProvideActions.getOauthProviders(),
                                OauthProvideActions.setIsSaving({ isSaving: false }),
                            ];
                        }),
                        catchError(response => {
                            return [
                                this.catchError(response),
                                OauthProvideActions.setIsSaving({ isSaving: false }),
                            ];
                        }),
                    ),
            ),
            share(),
        ),
    );

    public revokeOauthProvider$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OauthProvideActions.revokeOauthProvider),
            switchMap(({ id }) =>
                this.organizationService.revokeOauthProvider(id)
                    .pipe(
                        switchMap(res => {
                            return [
                                SharedActions.showMessage({ success: this.translateService.instant('OAUTH_PROVIDER_TOASTS.REVOKED_SUCCESS') }),
                            ];
                        }),
                        catchError(response => {
                            return [this.catchError(response)];
                        }),
                    ),
            ),
            share(),
        ),
    );

    public editOauthProvider$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OauthProvideActions.editOauthProvider),
            exhaustMap(({ id, dto }) =>
                [
                    OauthProvideActions.setIsSaving({ isSaving: true }),
                    OauthProvideActions.editOauthProviderServerCall({ id, dto }),
                ],
            ),
            share(),
        ),
    );

    public editOauthProviderServerCall$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OauthProvideActions.editOauthProviderServerCall),
            switchMap(({ id, dto }) =>
                this.organizationService.editOauthProvider(id, dto)
                    .pipe(
                        switchMap(res => {
                            return [
                                OauthProvideActions.editOauthProviderSuccess(),
                                SharedActions.showMessage({ success: this.translateService.instant('OAUTH_PROVIDER_TOASTS.EDITED_SUCCESS') }),
                                OauthProvideActions.resetList(),
                                OauthProvideActions.getOauthProviders(),
                                OauthProvideActions.setIsSaving({ isSaving: false }),
                            ];
                        }),
                        catchError(response => {
                            return [
                                this.catchError(response),
                                OauthProvideActions.setIsSaving({ isSaving: false }),
                            ];
                        }),
                    ),
            ),
            share(),
        ),
    );

    public getOauthProviderById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OauthProvideActions.getOauthProviderById),
            exhaustMap(({ id }) =>
                [
                    OauthProvideActions.getOauthProviderByIdServerCall({ id }),
                ],
            ),
            share(),
        ),
    );

    public getOauthProviderByIdServerCall$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OauthProvideActions.getOauthProviderByIdServerCall),
            switchMap(({ id }) =>
                this.organizationService.getOauthProviderById(id)
                    .pipe(
                        switchMap(oauthProvider => {
                            return [
                                OauthProvideActions.getOauthProviderByIdSuccess({ oauthProvider: oauthProvider }),
                            ];
                        }),
                        catchError(response => {
                            const message = this.utilsService.errMessage(response);
                            this.logger.error(message);

                            return [];
                        }),
                    ),
            ),
            share(),
        ),
    );

    public testOauthProvider$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OauthProvideActions.testOauthProvider),
            exhaustMap(({ id }) =>
                [
                    OauthProvideActions.setIsTesting({ isTesting: true }),
                    OauthProvideActions.testOauthProviderServerCall({ id }),
                ],
            ),
            share(),
        ),
    );

    public testOauthProviderServerCall$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OauthProvideActions.testOauthProviderServerCall),
            switchMap(({ id }) =>
                this.organizationService.testOauthProviderBy(id)
                    .pipe(
                        switchMap(res => {
                            return [
                                OauthProvideActions.setIsTesting({ isTesting: false }),
                                OauthProvideActions.testOauthProviderSuccess({ response: res.result }),
                                SharedActions.showMessage({ success: this.translateService.instant('OAUTH_PROVIDER_TOASTS.TESTED_SUCCESS') }),
                            ];
                        }),
                        catchError(response => {
                            return [
                                OauthProvideActions.testOauthProviderFail(),
                                OauthProvideActions.setIsTesting({ isTesting: false }),
                                this.catchError(response),
                            ];
                        }),
                    ),
            ),
            share(),
        ),
    );

    public filterTrigger$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                OauthProvideActions.setQuery,
                OauthProvideActions.setOrderDirection,
            ),
            debounceTime(400),
            switchMap(() => [
                // OauthProvideActions.setIsLoading({ isLoading: true }),
                OauthProvideActions.resetList(),
                OauthProvideActions.startLoadingOauthProviders(),
            ]),
        ),
    );

    constructor(
        private actions$: Actions,
        private store$: Store<AppState>,
        private organizationService: OrganizationService,
        private translateService: TranslateService,
        private utilsService: UtilsService,
    ) {
    }

    private catchError(response) {
        const message = this.utilsService.errMessage(response);
        return SharedActions.showMessage({ error: message });
    }
}